import { api } from './client'

/**
 * F-145 / TASK-0399 (REQ-153-001, REQ-056S-011) — tesoreria LETTA da conti.equalstudio.it. Il CRM non salva saldi e non
 * duplica banche, movimenti o riconciliazione: mostra cio' che dice conti. Se conti e' spento o non risponde, il server
 * risponde `available: false` con un motivo (mai un errore). Gli importi arrivano solo a chi ha accesso Amministrazione.
 */

export interface TreasuryAccount {
  externalId: string
  name: string
  kind: 'Azienda' | 'Carta'
  balance: number
}

export interface Treasury {
  source: 'conti'
  available: boolean
  reason: string | null
  message: string | null
  asOf: string | null
  accounts: TreasuryAccount[]
  liquidity: number | null
  vatDue: number | null
  taxesDue: number | null
  incomeMonth: number | null
  expensesMonth: number | null
  toClassify: number | null
}

export const TREASURY_HORIZONS = [7, 30, 60, 90] as const

export interface TreasuryOutlook {
  source: 'conti'
  /** Sempre `deterministico`: una proiezione, non una previsione statistica. */
  method: string
  available: boolean
  reason: string | null
  message: string | null
  asOf: string | null
  today: string
  until: string
  days: number
  liquidity: number | null
  expectedIn: number
  expectedOut: number
  recurringOut: number | null
  projected: number | null
  crmEntries: number
  recurringCount: number
}

/** F-152 / REQ-160-001 — la riserva minima è una configurazione (non un movimento). */
export interface TreasurySettings {
  minimumReserve: number
  horizonDays: number
  updatedAt: string | null
  updatedBy: number | null
}

export interface WaterfallDetail {
  label: string
  source: 'conti' | 'crm' | 'configurazione'
  amount: number
  count: number
  /** false = solo dettaglio, non entra nel totale del passo (anti-doppio sulle imposte). */
  counted: boolean
}

export interface WaterfallStep {
  /** "1".."9" e "7b" (altri impegni): i numeri del capitolo 161. */
  step: string
  code: string
  label: string
  source: 'conti' | 'crm' | 'configurazione'
  amount: number | null
  effect: 'start' | 'subtract' | 'result'
  count: number
  running: number | null
  note: string | null
  details: WaterfallDetail[]
}

/** F-152 / REQ-159-001, REQ-161-001 — la cascata dal saldo di conti alla liquidità libera gestionale. */
export interface TreasuryWaterfall {
  method: string
  available: boolean
  reason: string | null
  message: string | null
  asOf: string | null
  today: string
  until: string
  days: number
  minimumReserve: number
  /** Dopo i passi 2–7 (debiti maturati e impegni): null se conti non è disponibile. */
  netOfAccruals: number | null
  freeLiquidity: number | null
  steps: WaterfallStep[]
}

export interface DistributionShare {
  partnerId: string
  name: string
  quota: number
  amount: number
}

/** F-152 / REQ-197-001 — solo Admin. `label` è obbligatoria: una disponibilità gestionale, non un utile distribuibile. */
export interface DistributionScenario {
  label: string
  available: boolean
  reason: string | null
  message: string | null
  days: number | null
  freeLiquidity: number | null
  shares: DistributionShare[] | null
  quotaTotal: number | null
  unassigned: number | null
  conti: { available: number; taxesToCover: number; owedToThirdParties: number; companyCash: number; ok: boolean; label: string } | null
  note: string
}

export type OutlookGroupBy = 'day' | 'week' | 'month'

export interface OutlookPeriod {
  start: string
  end: string
  expectedIn: number
  certainIn: number
  expectedOut: number
  recurringOut: number
  taxCommitment: number
  balance: number | null
}

export interface TreasuryOutlookSeries {
  method: string
  available: boolean
  reason: string | null
  message: string | null
  days: number
  groupBy: OutlookGroupBy
  liquidity: number | null
  periods: OutlookPeriod[]
}

export const treasuryApi = {
  get: () => api<Treasury>('/api/finance/treasury'),
  outlook: (days: number) => api<TreasuryOutlook>(`/api/finance/treasury/outlook?days=${days}`),
  settings: () => api<TreasurySettings>('/api/finance/treasury/settings'),
  /** Solo Amministrazione/Admin (il server risponde 403 agli altri). Valori negativi: 400. */
  saveSettings: (body: { minimumReserve?: number; horizonDays?: number }) =>
    api<TreasurySettings>('/api/finance/treasury/settings', { method: 'PUT', body: JSON.stringify(body) }),
  waterfall: (days?: number) =>
    api<TreasuryWaterfall>(`/api/finance/treasury/waterfall${days ? `?days=${days}` : ''}`),
  distributionScenario: (days?: number) =>
    api<DistributionScenario>(`/api/finance/treasury/distribution-scenario${days ? `?days=${days}` : ''}`),
  outlookSeries: (groupBy: OutlookGroupBy, days?: number) =>
    api<TreasuryOutlookSeries>(`/api/finance/treasury/outlook-series?groupBy=${groupBy}${days ? `&days=${days}` : ''}`),
}

/** F-152 / REQ-132-001 — le posizioni dei soci lette da conti (spente di default; solo Admin e il socio per sé). */
export interface PartnerPositionRow {
  partnerId: string
  name: string
  quota: number
  /** Quota economica (spettante netto). */
  economicShare: number
  /** Saldo socio/società (positivo = ha in mano denaro della società). */
  accountPosition: number
  /** Già liquidato. */
  alreadyPaid: number
  /** Da liquidare. */
  toPay: number
}

export interface PartnerPositions {
  source: 'conti'
  label: string
  available: boolean
  reason: string | null
  message: string | null
  asOf: string | null
  rows: PartnerPositionRow[]
  /** Disponibilità distribuibile gestionale (con la stessa etichetta). */
  distributableAvailable: number | null
}

export interface MyPartnerPosition {
  source: 'conti'
  label: string
  available: boolean
  reason: string | null
  message: string | null
  asOf: string | null
  position: PartnerPositionRow | null
  distributableAvailable: number | null
}

export interface PartnerLink {
  userId: number
  contiPartnerId: string
  linkedAt: string
}

export const partnerPositionsApi = {
  list: () => api<PartnerPositions>('/api/finance/partner-positions'),
  /** 404 = utente non collegato a un socio (o lettura spenta). */
  me: () => api<MyPartnerPosition>('/api/finance/partner-positions/me'),
  links: () => api<PartnerLink[]>('/api/finance/partner-positions/links'),
  link: (userId: number, contiPartnerId: string) =>
    api<PartnerLink>(`/api/finance/partner-positions/links/${userId}`, { method: 'PUT', body: JSON.stringify({ contiPartnerId }) }),
  unlink: (userId: number) => api<void>(`/api/finance/partner-positions/links/${userId}`, { method: 'DELETE' }),
}
